import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import type {
  AuditResponse,
  ContractDetail,
  SendResponse,
  TemplateDetail,
} from '../api/client';
import { ApiError, useAuth } from '../auth/AuthProvider';
import { AuditViewer } from '../components/AuditViewer';
import { StatusBadge } from '../components/StatusBadge';
import { formatDate } from '../lib/format';

export function ContractDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { fetcher } = useAuth();
  const qc = useQueryClient();
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signingUrl, setSigningUrl] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const contractQ = useQuery({
    queryKey: ['contract', id],
    queryFn: () => fetcher<ContractDetail>(`/api/contracts/${id}`),
  });
  const auditQ = useQuery({
    queryKey: ['contract', id, 'audit'],
    queryFn: () => fetcher<AuditResponse>(`/api/contracts/${id}/audit`),
  });
  const templateQ = useQuery({
    queryKey: ['template', contractQ.data?.template_id],
    queryFn: () =>
      fetcher<TemplateDetail>(`/api/templates/${contractQ.data!.template_id}`),
    enabled: !!contractQ.data?.template_id,
  });

  const onMutate = {
    onSuccess: () => {
      setActionError(null);
      void qc.invalidateQueries({ queryKey: ['contract', id] });
      void qc.invalidateQueries({ queryKey: ['contract', id, 'audit'] });
    },
    onError: (err: unknown) => {
      setActionError(
        err instanceof ApiError ? err.message : (err as Error).message,
      );
    },
  };

  const sendM = useMutation({
    mutationFn: () =>
      fetcher<SendResponse>(`/api/contracts/${id}/send`, {
        method: 'POST',
        body: JSON.stringify({
          name: signerName,
          email: signerEmail || undefined,
        }),
      }),
    onSuccess: (res) => {
      setSigningUrl(res.signing_url);
      onMutate.onSuccess();
    },
    onError: onMutate.onError,
  });
  const voidM = useMutation({
    mutationFn: () =>
      fetcher(`/api/contracts/${id}/void`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'voided from UI' }),
      }),
    ...onMutate,
  });
  const completeM = useMutation({
    mutationFn: () =>
      fetcher(`/api/contracts/${id}/complete`, {
        method: 'POST',
        body: JSON.stringify({ note: 'completed from UI' }),
      }),
    ...onMutate,
  });
  const redeliverM = useMutation({
    mutationFn: () =>
      fetcher(`/api/contracts/${id}/redeliver`, { method: 'POST' }),
    ...onMutate,
  });
  const syncM = useMutation({
    mutationFn: () =>
      fetcher(`/api/contracts/${id}/sync`, { method: 'POST' }),
    ...onMutate,
  });

  if (contractQ.isLoading) return <p>Loading…</p>;
  if (contractQ.isError || !contractQ.data) {
    return <p className="text-red-600">Failed to load contract.</p>;
  }
  const c = contractQ.data;

  const templateChanged =
    templateQ.data && c.template_updated_at && c.status === 'draft'
      ? new Date(templateQ.data.updated_at).getTime() >
        new Date(c.template_updated_at).getTime()
      : false;

  async function openDocument(kind: 'document' | 'signed-document') {
    // Phase 11: server routes GET /api/contracts/:id/document and
    // /signed-document return a presigned S3 URL (1h TTL). 409 if the
    // document hasn't been assembled yet; 503 if storage isn't configured.
    try {
      const r = await fetcher<{ url?: string }>(`/api/contracts/${id}/${kind}`);
      // G4 — 'noopener' severs window.opener so the presigned-URL tab can't
      // navigate this one (reverse tabnabbing).
      if (r?.url) window.open(r.url, '_blank', 'noopener');
    } catch (err) {
      setActionError(
        err instanceof ApiError
          ? `${err.status}: ${err.message}`
          : (err as Error).message,
      );
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold">Contract {c.id.slice(0, 8)}</h1>
        <StatusBadge status={c.status} />
      </div>
      {actionError ? (
        <p className="text-sm text-red-600" role="alert">
          {actionError}
        </p>
      ) : null}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="card lg:col-span-2 space-y-2">
          <h2 className="text-sm font-medium uppercase text-slate-500">
            Timeline
          </h2>
          <ul className="space-y-1 text-sm">
            <li>Created: {formatDate(c.created_at)}</li>
            <li>Sent: {formatDate(c.sent_at)}</li>
            <li>Signed: {formatDate(c.signed_at)}</li>
            <li>Voided: {formatDate(c.voided_at)}</li>
            <li>Declined: {formatDate(c.declined_at)}</li>
            <li>Completed: {formatDate(c.completed_at)}</li>
          </ul>
          {templateChanged ? (
            <p className="rounded bg-amber-50 p-2 text-sm text-amber-800">
              The source template has changed since this draft was created.
              <button
                type="button"
                className="btn ml-2"
                onClick={() => syncM.mutate()}
              >
                Sync to latest
              </button>
            </p>
          ) : null}
        </div>
        <div className="card space-y-2">
          <h2 className="text-sm font-medium uppercase text-slate-500">
            Actions
          </h2>
          {c.status === 'draft' ? (
            <div className="space-y-2">
              <input
                className="input"
                placeholder="Signer name"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
              />
              <input
                className="input"
                placeholder="Signer email"
                value={signerEmail}
                onChange={(e) => setSignerEmail(e.target.value)}
              />
              <button
                type="button"
                className="btn-primary w-full"
                disabled={!signerName.trim() || sendM.isPending}
                onClick={() => sendM.mutate()}
              >
                Send
              </button>
              <button
                type="button"
                className="btn-danger w-full"
                onClick={() => voidM.mutate()}
              >
                Void
              </button>
            </div>
          ) : null}
          {(c.status === 'sent' || c.status === 'viewed') && (
            <button
              type="button"
              className="btn-danger w-full"
              onClick={() => voidM.mutate()}
            >
              Void
            </button>
          )}
          {c.status === 'signed' && (
            <button
              type="button"
              className="btn-primary w-full"
              onClick={() => completeM.mutate()}
            >
              Mark complete
            </button>
          )}
          <button
            type="button"
            className="btn w-full"
            onClick={() => redeliverM.mutate()}
          >
            Redeliver webhook
          </button>
          <button
            type="button"
            className="btn w-full"
            onClick={() => openDocument('document')}
          >
            Download original
          </button>
          <button
            type="button"
            className="btn w-full"
            onClick={() => openDocument('signed-document')}
          >
            Download signed
          </button>
          {signingUrl ? (
            <div className="rounded bg-emerald-50 p-2 text-xs">
              <p className="font-medium">Signing URL:</p>
              <a
                href={signingUrl}
                target="_blank"
                rel="noreferrer"
                className="break-all text-brand underline"
              >
                {signingUrl}
              </a>
            </div>
          ) : null}
        </div>
      </div>
      <div className="card">
        <h2 className="mb-2 text-sm font-medium uppercase text-slate-500">
          Document
        </h2>
        <iframe
          title="document"
          sandbox=""
          srcDoc={c.template_snapshot ?? ''}
          className="h-96 w-full rounded border border-slate-200"
        />
      </div>
      <div className="card">
        <h2 className="mb-2 text-sm font-medium uppercase text-slate-500">
          Audit trail
        </h2>
        {auditQ.isLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : auditQ.data ? (
          <AuditViewer data={auditQ.data} />
        ) : (
          <p className="text-sm text-red-600">Failed to load audit.</p>
        )}
      </div>
    </div>
  );
}
